import React from 'react';
import { Skeleton } from 'primereact/skeleton';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import PieChartWithLegend from '../../../../../shared/components/chart/pie-chart-with-legend/PieChartWithLegend';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import ComputingState from '../../../../../shared/components/common/computing-state/ComputingState';
import { PieChartData } from '../../../../../shared/models/common/PieChartData';
import { t } from 'i18next';
import { SOV_PIE_CHART_PALETTE } from '../../../../../shared/utils/sovPalette';
import usePermission from '../../../../../shared/hooks/global/UsePermission';

interface ShareOfVoiceChartProps {
  shareOfVoiceData: PieChartData[] | undefined;
  hasAnyPendingQuestions: boolean;
  firmName: string;
  hasProjectStatsData: boolean;
  onSettingsClick: () => void;
  isLoading: boolean;
  recomputingBrands?: string[];
}

const ShareOfVoicePieChart: React.FC<ShareOfVoiceChartProps> = ({
  shareOfVoiceData,
  hasAnyPendingQuestions,
  firmName,
  hasProjectStatsData,
  onSettingsClick,
  isLoading,
  recomputingBrands = [],
}) => {
  const projectsAccess = usePermission('PROJECTS');
  const hasData = !!shareOfVoiceData && shareOfVoiceData.length > 0;
  const hasOnlyOthers =
    shareOfVoiceData && shareOfVoiceData.length === 1 && shareOfVoiceData[0].name === t('others');
  const isCollecting = !hasProjectStatsData && hasAnyPendingQuestions;
  return (
    <div className={`stats__panel${isCollecting ? ' stats__panel--computing' : ''}`}>
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('shareOfVoice')}
          <CustomTooltip tooltipText="shareOfVoiceTooltip" addMargin={false} />
        </div>
      </div>
      {recomputingBrands.length > 0 ? (
        <div className="stats-box stats-box--pie-chart">
          <div className="stats-box__content">
            <ComputingState
              title="statsRecomputingTitle"
              subtitle="statsRecomputingSubtitle"
              subtitleParams={{ brands: recomputingBrands.join(', ') }}
            />
          </div>
        </div>
      ) : isLoading ? (
        <div className="stats-box stats-box--pie-chart">
          <div className="stats-box__content">
            <Skeleton className="skeleton--chart-pie">
              <span />
              <span />
              <span />
            </Skeleton>
          </div>
        </div>
      ) : !hasProjectStatsData && hasAnyPendingQuestions ? (
        // Fresh project still collecting → friendly "preparing benchmark" state instead of a skeleton.
        <div className="stats-box stats-box--pie-chart">
          <div className="stats-box__content">
            <ComputingState title="computingSovTitle" variant="benchmark" />
          </div>
        </div>
      ) : !hasData || hasOnlyOthers ? (
        // No brands to show (empty project, or the selected model/filter has no data) and nothing is
        // still loading/pending → empty state, instead of a blank chart.
        <div className="stats-box stats-box--pie-chart stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--pie-chart">
          <div className="stats-box__content">
            {/* Wybór marek do porównania zapisuje się w opcjach projektu — to zapis, nie widok. */}
            {projectsAccess.write && (
              <>
                <Button id="btn-settings-pie" className="action-btn" onClick={onSettingsClick}>
                  <span className="ico--settings ico--xs" />
                </Button>
                <CustomTooltip tooltipText="chooseBrandsToCompare" targetRef="btn-settings-pie" />
              </>
            )}
            <PieChartWithLegend
              chartData={shareOfVoiceData!}
              colorPalette={SOV_PIE_CHART_PALETTE}
              selectedBrand={firmName}
              size="SM"
              capitalizeBrands
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareOfVoicePieChart;
